import { useState, useEffect, useCallback, useRef } from 'react'
import { listIngestionRuns, getIngestionRun, listIngestionIssues } from '../services/ingestion'
import type { IngestionRun, IngestionIssuesResponse } from '../types/ingestion'
import { isActiveIngestionStatus } from '../types/ingestion'
import { useInvalidationSubscription } from './use-list-invalidation'
import { usePollingRefetch } from './use-polling-refetch'

interface UseIngestionRunsResult {
  runs: IngestionRun[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useIngestionRuns(
  caseId: string | null,
  statusFilter?: string
): UseIngestionRunsResult {
  const [runs, setRuns] = useState<IngestionRun[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Only the first load per (case, filter) shows the placeholder; polling and
  // invalidation refreshes update the list in place without a loading flash.
  const loadedKeyRef = useRef<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setRuns([])
      setError(null)
      loadedKeyRef.current = null
      return
    }

    const key = `${caseId}:${statusFilter ?? ''}`
    if (loadedKeyRef.current !== key) setLoading(true)
    setError(null)

    try {
      const data = await listIngestionRuns(caseId, statusFilter)
      setRuns(data)
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : 'Failed to fetch ingestion runs')
      setRuns([])
    } finally {
      loadedKeyRef.current = key
      setLoading(false)
    }
  }, [caseId, statusFilter])

  useEffect(() => {
    void fetch()
  }, [fetch])

  useInvalidationSubscription('ingestion_runs', fetch)

  return { runs, loading, error, refetch: fetch }
}

interface UseIngestionRunResult {
  run: IngestionRun | null
  issues: IngestionIssuesResponse | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useIngestionRun(
  caseId: string | null,
  runId: string | null
): UseIngestionRunResult {
  const [run, setRun] = useState<IngestionRun | null>(null)
  const [issues, setIssues] = useState<IngestionIssuesResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Only the first load per run shows the placeholder; live polling refreshes
  // the panel in place without blanking it every tick.
  const loadedKeyRef = useRef<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !runId) {
      setRun(null)
      setIssues(null)
      setError(null)
      loadedKeyRef.current = null
      return
    }

    const key = `${caseId}:${runId}`
    if (loadedKeyRef.current !== key) setLoading(true)
    setError(null)

    try {
      const [runData, issuesData] = await Promise.all([
        getIngestionRun(caseId, runId),
        listIngestionIssues({ caseId, runId }),
      ])
      setRun(runData)
      setIssues(issuesData)
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : 'Failed to fetch ingestion run')
      setRun(null)
      setIssues(null)
    } finally {
      loadedKeyRef.current = `${caseId}:${runId}`
      setLoading(false)
    }
  }, [caseId, runId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  // Keep the detail panel live while the run is still in progress; stops
  // automatically once the run reaches a terminal status.
  usePollingRefetch(fetch, run !== null && isActiveIngestionStatus(run.status))

  return { run, issues, loading, error, refetch: fetch }
}
